import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { registrationService } from '../services/registrationService';
import { config } from '../config/environment';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'login' | 'register';
type RegStatus = 'idle' | 'loading' | 'available' | 'existing_client' | 'existing_coach';

export default function CoachLoginDrawer({ isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('login');

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot password state
  const [forgot, setForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Registration state
  const [regEmail, setRegEmail] = useState('');
  const [regStatus, setRegStatus] = useState<RegStatus>('idle');
  const [regClientData, setRegClientData] = useState<{ firstName?: string; lastName?: string; phone?: string }>({});
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpChecking, setOtpChecking] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen) {
      setTab('login');
      setEmail(''); setPassword(''); setError(''); setShowPw(false);
      setForgot(false); setResetEmail(''); setResetMsg('');
      resetReg();
    }
  }, [isOpen]);

  const resetReg = () => {
    setRegEmail(''); setRegStatus('idle'); setRegClientData({});
    setOtpCode(''); setOtpVerified(false); setOtpError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const response = await registrationService.loginCoach(email, password);
      if (response.success) {
        if (response.token) {
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('userType', 'coach');
          localStorage.setItem('userData', JSON.stringify({ ...response.user, must_verify: response.mustVerify }));
        }
        onClose();
        if (response.mustVerify) { navigate('/verify', { replace: true }); return; }
        navigate('/dashboard/coach', { replace: true });
      } else {
        setError(response.message || 'Login failed');
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true); setResetMsg('');
    try {
      const result = await registrationService.requestPasswordReset(resetEmail);
      setResetMsg(result.message || 'Reset link sent.');
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const sendOtp = async (emailAddr: string) => {
    setOtpSending(true); setOtpError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/verify/pre-register/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddr }),
      });
      if (!res.ok) {
        const data = await res.json();
        setOtpError(data.detail || 'Failed to send verification code');
      }
    } catch {
      setOtpError('Failed to send verification code');
    } finally {
      setOtpSending(false);
    }
  };

  const checkOtp = async () => {
    setOtpChecking(true); setOtpError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/verify/pre-register/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: regEmail, code: otpCode }),
      });
      if (res.ok) {
        setOtpVerified(true);
      } else {
        const data = await res.json();
        setOtpError(data.detail || 'Invalid or expired code');
      }
    } catch {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setOtpChecking(false);
    }
  };

  const handleEmailCheck = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegStatus('loading');
    try {
      const res = await fetch(`${config.apiBaseUrl}/coaches/check-email?email=${encodeURIComponent(regEmail)}`);
      const data = await res.json();
      setRegStatus(data.status);
      if (data.status === 'existing_client') {
        setRegClientData({ firstName: data.first_name, lastName: data.last_name, phone: data.phone });
      }
      if (data.status === 'available') {
        setOtpCode(''); setOtpVerified(false); setOtpError('');
        await sendOtp(regEmail);
      }
    } catch {
      setRegStatus('available');
      setOtpCode(''); setOtpVerified(false); setOtpError('');
      await sendOtp(regEmail);
    }
  };

  const handleRegisterProceed = () => {
    const prefill = regStatus === 'existing_client' ? { ...regClientData, email: regEmail } : undefined;
    const emailToPass = regEmail;
    onClose();
    navigate('/register/provider', { state: { email: emailToPass, prefill } });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 h-full w-full max-w-sm z-50 flex flex-col bg-gradient-to-b from-indigo-950 via-violet-950 to-fuchsia-950 shadow-2xl overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-fuchsia-400 to-violet-500 rounded-lg flex items-center justify-center">
                  <SparklesIcon className="w-4 h-4 text-white" />
                </div>
                <span className="text-white font-semibold text-sm">CoachSight ECR</span>
              </div>
              <button onClick={onClose} className="text-violet-400 hover:text-white transition-colors">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/10 mx-6 mt-6">
              {(['login', 'register'] as Tab[]).map(t => (
                <button
                  key={t}
                  onClick={() => { setTab(t); setError(''); setForgot(false); setResetMsg(''); resetReg(); }}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    tab === t
                      ? 'border-fuchsia-400 text-white'
                      : 'border-transparent text-violet-500 hover:text-violet-300'
                  }`}
                >
                  {t === 'login' ? 'Log In' : 'Register'}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 px-6 py-8">
              <AnimatePresence mode="wait">
                {tab === 'login' ? (
                  <motion.div
                    key="login"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-9 h-9 bg-fuchsia-500/20 rounded-xl flex items-center justify-center">
                        <UserGroupIcon className="w-5 h-5 text-fuchsia-400" />
                      </div>
                      <h2 className="text-xl font-bold text-white">
                        {forgot ? 'Reset password' : 'Coach login'}
                      </h2>
                    </div>

                    {forgot ? (
                      <form onSubmit={handleForgot} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-200 mb-1">Email address</label>
                          <input
                            type="email"
                            value={resetEmail}
                            onChange={e => setResetEmail(e.target.value)}
                            className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                            placeholder="your@email.com"
                            required
                            autoFocus
                          />
                        </div>
                        {resetMsg && (
                          <div className="p-3 bg-blue-900/30 border border-blue-800 rounded-xl">
                            <p className="text-blue-300 text-sm">{resetMsg}</p>
                          </div>
                        )}
                        <button
                          type="submit"
                          disabled={resetLoading}
                          className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${resetLoading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}
                        >
                          {resetLoading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Sending…</> : 'Send Reset Link'}
                        </button>
                        <button type="button" onClick={() => { setForgot(false); setResetMsg(''); }} className="w-full text-sm text-violet-400 hover:text-violet-300 transition-colors">
                          Back to login
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                          <input
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                            placeholder="your@email.com"
                            required
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-200 mb-1">Password</label>
                          <div className="relative">
                            <input
                              type={showPw ? 'text' : 'password'}
                              value={password}
                              onChange={e => setPassword(e.target.value)}
                              className="w-full px-3 py-2.5 pr-10 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                              placeholder="Enter your password"
                              required
                            />
                            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                              {showPw ? <EyeSlashIcon className="h-4 w-4 text-violet-500" /> : <EyeIcon className="h-4 w-4 text-violet-500" />}
                            </button>
                          </div>
                        </div>
                        {error && (
                          <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl">
                            <p className="text-red-300 text-sm">{error}</p>
                          </div>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${loading ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}
                        >
                          {loading ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Signing in…</> : <><KeyIcon className="w-4 h-4 mr-2" />Sign in</>}
                        </button>
                        <div className="flex items-center justify-between text-xs">
                          <button type="button" onClick={() => setForgot(true)} className="text-violet-400 hover:text-violet-300 transition-colors">
                            Forgot password?
                          </button>
                          <button type="button" onClick={() => setTab('register')} className="text-fuchsia-400 hover:text-fuchsia-300 font-medium transition-colors">
                            Register as coach
                          </button>
                        </div>
                      </form>
                    )}

                    <p className="mt-8 text-center text-xs text-violet-600">
                      Client?{' '}
                      <a href="/login" className="text-violet-400 hover:text-violet-300 transition-colors">
                        Sign in here
                      </a>
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="register"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.18 }}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-9 h-9 bg-fuchsia-500/20 rounded-xl flex items-center justify-center">
                        <SparklesIcon className="w-5 h-5 text-fuchsia-400" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white">Register as Coach</h2>
                        <p className="text-violet-400 text-xs mt-0.5">Enter your email to get started</p>
                      </div>
                    </div>

                    {regStatus === 'idle' || regStatus === 'loading' ? (
                      <form onSubmit={handleEmailCheck} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-200 mb-1">Email</label>
                          <input
                            type="email"
                            value={regEmail}
                            onChange={e => setRegEmail(e.target.value)}
                            className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                            placeholder="your@email.com"
                            required
                            autoFocus
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={regStatus === 'loading'}
                          className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${regStatus === 'loading' ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}
                        >
                          {regStatus === 'loading' ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Checking…</> : 'Continue'}
                        </button>
                      </form>
                    ) : regStatus === 'available' ? (
                      <div className="space-y-4">
                        {otpVerified ? (
                          <>
                            <div className="flex items-center gap-2 p-3 bg-green-900/30 border border-green-700 rounded-xl">
                              <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                              <p className="text-green-300 text-sm">Email verified! Let's set up your coach profile.</p>
                            </div>
                            <button onClick={handleRegisterProceed} className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white">
                              Continue to Registration
                            </button>
                          </>
                        ) : (
                          <>
                            <p className="text-violet-300 text-sm text-center">
                              {otpSending ? 'Sending verification code…' : `We sent a 6-digit code to ${regEmail}`}
                            </p>
                            <div>
                              <label className="block text-sm font-medium text-gray-200 mb-1">Verification Code</label>
                              <input
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={otpCode}
                                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                                className="w-full px-3 py-2.5 border border-white/10 bg-white/5 text-gray-200 placeholder-violet-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-fuchsia-500 text-center tracking-widest text-lg"
                                placeholder="000000"
                                autoFocus
                              />
                            </div>
                            {otpError && <p className="text-red-400 text-sm">{otpError}</p>}
                            <button
                              type="button"
                              onClick={checkOtp}
                              disabled={otpCode.length !== 6 || otpChecking}
                              className={`w-full flex items-center justify-center py-2.5 rounded-xl font-semibold text-sm ${otpCode.length !== 6 || otpChecking ? 'bg-gray-600 cursor-not-allowed text-gray-400' : 'bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white'}`}
                            >
                              {otpChecking ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Verifying…</> : 'Verify Code'}
                            </button>
                            <button type="button" onClick={() => sendOtp(regEmail)} disabled={otpSending} className="w-full text-sm text-fuchsia-400 hover:text-fuchsia-300 transition-colors disabled:opacity-50">
                              {otpSending ? 'Sending…' : 'Resend code'}
                            </button>
                          </>
                        )}
                        <button type="button" onClick={resetReg} className="w-full text-sm text-violet-500 hover:text-violet-300 transition-colors">
                          ← Use a different email
                        </button>
                      </div>
                    ) : regStatus === 'existing_client' ? (
                      <div className="space-y-4">
                        <div className="p-3 bg-violet-900/30 border border-violet-700 rounded-xl">
                          <p className="text-violet-200 text-sm">You already have a client account with this email. You can add a coach profile to your existing account.</p>
                        </div>
                        <button onClick={handleRegisterProceed} className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white">
                          Yes, Add Coach Profile
                        </button>
                        <button type="button" onClick={resetReg} className="w-full text-sm text-violet-500 hover:text-violet-300 transition-colors">
                          ← Use a different email
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-3 bg-red-900/30 border border-red-800 rounded-xl">
                          <p className="text-red-300 text-sm">A coach account already exists for this email. Please sign in instead.</p>
                        </div>
                        <button type="button" onClick={() => { resetReg(); setTab('login'); }} className="w-full py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white">
                          Go to Log In
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
